/**
 * Theme Initializer
 * Detects brand from URL and applies corresponding CSS variables
 * 
 * Usage:
 * import { initializeTheme } from '@/utils/themeInitializer'
 * initializeTheme()
 */

/**
 * Theme configuration for different brands
 */
const THEME_CONFIG = {
    comda: {
        '--brand-primary': '#0D72B1',
        '--brand-primary-dark': '#0052a3',
        '--brand-primary-light': '#0072BC',
        '--brand-primary-lighter': '#F0F8FF',
        '--brand-secondary': '#0054a3',
        '--brand-accent': '#0066cc',
        '--font-family-brand': "'Open Sans', sans-serif",
        '--shadow-color': 'rgba(13, 114, 177, 0.1)',
        '--title-section':'#034EA2'
    },
    mlp: {
        '--brand-primary': '#009444',
        '--brand-primary-dark': '#007a38',
        '--brand-primary-light': '#00a84f',
        '--brand-primary-lighter': '#e8f5e9',
        '--brand-secondary': '#009444',
        '--brand-accent': '#00b853',
        '--font-family-brand': "'museo-sans', sans-serif",
        '--shadow-color': 'rgba(0, 148, 68, 0.1)',
        '--title-section':'#009444'

    }
};

/**
 * Detects current brand based on URL
 * @returns {string} 'comda' or 'mlp'
 */
export function detectBrand() {
    const url = window.location.href.toLowerCase();
    const isComda = url.includes('comda');
    return isComda ? 'comda' : 'mlp';
}

/**
 * Applies theme to document root
 * @param {string} brand - Brand name ('comda' or 'mlp')
 */
export function applyTheme(brand) {
    const root = document.documentElement;
    const theme = THEME_CONFIG[brand];

    if (!theme) {
        console.error(`[Theme] Unknown brand: ${brand}, falling back to 'comda'`);
        brand = 'comda';
    }

    // Set data attribute for CSS selectors
    root.setAttribute('data-brand', brand);

    // Apply all CSS variables
    Object.entries(THEME_CONFIG[brand]).forEach(([property, value]) => {
        root.style.setProperty(property, value);
    });

    console.log(`🎨 [Theme] Initialized ${brand.toUpperCase()} theme`);
    
    // Additional debug log (can be removed in production)
    if (process.env.NODE_ENV === 'development') {
        console.log('[Theme] Applied CSS variables:', THEME_CONFIG[brand]);
    }
}

/**
 * Main theme initialization function
 * Detects brand and applies corresponding theme
 * 
 * @returns {string} Detected brand
 */
export function initializeTheme() {
    const brand = detectBrand();
    applyTheme(brand);
    return brand;
}

/**
 * Gets current active brand from DOM
 * @returns {string|null} Current brand or null if not set
 */
export function getCurrentBrand() {
    return document.documentElement.getAttribute('data-brand');
}

/**
 * Gets CSS variable value for current theme
 * @param {string} variableName - CSS variable name (e.g., '--brand-primary')
 * @returns {string} CSS variable value
 */
export function getThemeVariable(variableName) {
    return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

/**
 * Export theme configuration (for use in other places)
 */
export { THEME_CONFIG };
